import regreisson, { DataPoint, Result } from "regression";
import { SpuPointsDataset } from "./stores/spuPoints";
import { DataSource, Dataset, CurveModel, ProccessedSpuData } from "./types";

import _ from 'lodash';

// 求解二次型
const solveQuadratic = (a: number, b: number, c: number): number | void => {
  const delta = b ** 2 - 4 * a * c; // b² - 4ac

  if (delta < 0) {
    return;
  }

  const sqrtDelta = Math.sqrt(delta); // √￣b² - 4ac

  const add = -b + sqrtDelta;
  const subtract = -b - sqrtDelta;

  const denominator = 2 * a; // 分母

  if (delta === 0) {
    return add / denominator;
  }

  const x1 = add / denominator;
  const x2 =  subtract / denominator;
  const result = Math.max(x1, x2);

  if (result < 0) {
    return;
  }

  return result;
};

// 求解四次方程 - 改进版
const shortestSolveQuartic = (
  a: number,
  b: number,
  c: number,
  d: number,
  e: number,
  center: number = 0
): number | void => {
  const df = (x: number) => a * x ** 4 + b * x ** 3 + c * x ** 2 + d * x + e;
  const dx = (x: number) => 4 * a * x ** 3 + 3 * b * x ** 2 + 2 * c * x + d;

  // 扩大初始值搜索范围
  const initialGuesses = [
    center, 
    center * 0.5, 
    center * 0.8, 
    center * 1.2, 
    center * 1.5, 
    center * 2,
    center * 0.3,
    center * 3
  ];
  const results: number[] = [];

  for (const initialGuess of initialGuesses) {
    for (let x = initialGuess, xn = 0, i = 0; i < 100; x = xn, i += 1) {
      const dxValue = dx(x);
      
      // 避免除零
      if (Math.abs(dxValue) < 1e-10) {
        break;
      }
      
      xn = x - df(x) / dxValue;

      if (Math.abs(x - xn) < 1e-9) {
        // 检查是否为有效根（正值且函数值接近0）
        if (xn > 0 && Math.abs(df(xn)) < 1e-6) {
          results.push(xn);
        }
        break;
      }
    }
  }

  if (results.length === 0) {
    return;
  }

  // 返回最接近 center 的有效根
  return results.reduce((closest, current) => {
    return Math.abs(current - center) < Math.abs(closest - center) ? current : closest;
  });
};

// 查找交点 [获取辅助线与QH1的交点]
export const findIntersectionPoint = (
  q: number,
  h: number,
  equation: Array<number>,
  type: number = 0,
  center: number = 0
): Array<number> | void => {
  const [a, b, c] = equation;
  let k, q1, h1;

  if (type === 0) { // 公式1: H = k×Q²
    k = h / q ** 2;

    // 联立: aQ² + bQ + c = k×Q²
    // 整理: (a-k)Q² + bQ + c = 0
    q1 = solveQuadratic(a - k, b, c);
  } else { // 公式2: H² = k×Q³
    k = Math.pow(h, 2) / Math.pow(q, 3);

    // 联立: (aQ² + bQ + c)² = k×Q³
    // 展开: a²Q⁴ + 2abQ³ + (b²+2ac)Q² + 2bcQ + c² - kQ³ = 0
    // 整理: a²Q⁴ + (2ab-k)Q³ + (b²+2ac)Q² + 2bcQ + c² = 0
    q1 = shortestSolveQuartic(
      a ** 2,
      2 * a * b - k,
      b ** 2 + 2 * a * c,
      2 * b * c,
      c ** 2,
      center
    );
  }

  if (!q1) {
    // 无法求解交点，使用原始点作为fallback
    return [q, h];
  }

  // 计算 h1
  h1 = q1 ** 2 * a + q1 * b + c;

  // 验证结果的合理性
  let isValid = false;
  
  if (type === 0) {
    // 验证 H = k×Q²
    const expectedH = k * q1 ** 2;
    const error = Math.abs(h1 - expectedH);
    
    if (error <= 0.1) {
      isValid = true;
    } else {
      // console.warn('Type 0: 交点验证失败', { q1, h1, expectedH, error });
    }
  } else {
    // 验证 H² = k×Q³
    const expectedH2 = k * q1 ** 3;
    const actualH2 = h1 ** 2;
    const error = Math.abs(actualH2 - expectedH2);
    
    if (error <= 0.1) {
      isValid = true;
    } else {
      // console.warn('Type 1: 交点验证失败', { q1, h1, expectedH2: Math.sqrt(expectedH2), actualH2, error });
    }
  }

  // 如果验证失败，尝试备用方案
  if (!isValid) {
    // 先使用不同的center值重新计算
    const alternativeCenters = [q, q * 0.5, q * 1.5, q * 2];
    
    for (const altCenter of alternativeCenters) {
      let altQ1;
      
      if (type === 0) {
        altQ1 = solveQuadratic(a - k, b, c);
      } else {
        altQ1 = shortestSolveQuartic(
          a ** 2,
          2 * a * b - k,
          b ** 2 + 2 * a * c,
          2 * b * c,
          c ** 2,
          altCenter
        );
      }
      
      if (altQ1) {
        const altH1 = altQ1 ** 2 * a + altQ1 * b + c;
        
        // 再次验证
        let altValid = false;
        if (type === 0) {
          const expectedH = k * altQ1 ** 2;
          altValid = Math.abs(altH1 - expectedH) <= 0.1;
        } else {
          const expectedH2 = k * altQ1 ** 3;
          const actualH2 = altH1 ** 2;
          altValid = Math.abs(actualH2 - expectedH2) <= 0.1;
        }
        
        if (altValid) {
          return [altQ1, altH1];
        }
      }
    }
    
    // 如果所有尝试都失败，返回一个合理的估算值
    
    // 基于原始点按比例缩放
    const scaleFactor = type === 0 ? 1.02 : 1.05;
    const fallbackQ = q * scaleFactor;
    const fallbackH = fallbackQ ** 2 * a + fallbackQ * b + c;
    
    return [fallbackQ, fallbackH];
  }

  // console.log('找到交点:', { type, q1, h1, k });

  return [q1, h1];
};

// 回归模型
export const genRegressionModal = (
  dataset: DataPoint[],
  cbrt: any = false,
  isSelected: any = false,
  order: number = 2
): CurveModel => {
  // 对于高次多项式（4次及以上），使用Catmull-Rom样条插值代替多项式回归
  // 这样可以避免过拟合，保证曲线平滑且不会产生异常值
  if (order >= 4) {
    // console.log('使用Catmull-Rom样条插值代替', order, '次多项式');
    
    // 使用Catmull-Rom样条插值
    const splineResult: any = generateCatmullRomPoints(dataset, 500);
    const dummyPoints = splineResult.dummyPoints;
    
    // 创建一个基于插值点的预测函数
    const interpolatePredict = (x: number) => {
      // 在插值点中查找最接近的点
      if (x <= dummyPoints[0][0]) return dummyPoints[0][1];
      if (x >= dummyPoints[dummyPoints.length - 1][0]) return dummyPoints[dummyPoints.length - 1][1];
      
      // 线性插值
      for (let i = 0; i < dummyPoints.length - 1; i++) {
        if (x >= dummyPoints[i][0] && x <= dummyPoints[i + 1][0]) {
          const x0 = dummyPoints[i][0];
          const y0 = dummyPoints[i][1];
          const x1 = dummyPoints[i + 1][0];
          const y1 = dummyPoints[i + 1][1];
          const t = (x - x0) / (x1 - x0);
          return y0 + t * (y1 - y0);
        }
      }
      
      return dummyPoints[dummyPoints.length - 1][1];
    };
    
    return {
      equation: [], // 样条插值没有方程系数
      predict: (x: number) => [x, interpolatePredict(x)],
      points: dataset,
      dummyPoints: dummyPoints,
      string: 'Catmull-Rom Spline Interpolation',
      r2: 1 // 样条插值完美通过所有点，r2为1
    };
  }
  
  // 对于2次和3次多项式，继续使用原来的回归方法
  const result = regreisson.polynomial(dataset, {
    order: order,
    precision: 10 // 精度
  });

  const {
    predict,
    points
  } = result;

  const firstPoint = points[0];
  const lastPoint = points[points.length - 1];

  // 根据第一个点，和最后一个点，中间找到100个点，来绘制canvas line
  return {
    ...result,
    dummyPoints: generateData(
      (x) => predict(x)[1],
      firstPoint[0],
      lastPoint[0],
      (lastPoint[0] - firstPoint[0]) / 100,
      cbrt,
      isSelected
    ),
  };
};

export const precision = (n: number, p: number): number => {
  const finalN = Number(n)

  // 当精度为 0 的时候，四舍五入取整
  if (p === 0) return Math.round(finalN);

  // 否则按照精度，在小数位四舍五入取整
  return Number(finalN.toFixed(p));
};

const ceil10 = (n: number): number => {
  if (n % 10 === 0) {
    return n;
  }

  return n - (n % 10) + 10;
};

// 计算 Y 轴
export const calcYAxis = (
  Y: Array<number>, // 数据
  splitNumber: number, // 比例尺
  padding?: number // 间隔
): [max: number, interval: number] => {
  let max = Math.max(...Y);

  if (padding) {
    max /= (splitNumber - padding) / splitNumber;
  }

  max = max < 10 ? Math.ceil(max) : ceil10(max);
  const interval = max / splitNumber;

  return [max, interval];
};

// 变成数组
export const zip = (a: Array<any>, b: Array<any>): Array<any> =>
  a.map((e, i) => [e, b[i]]);

export const mapand = (
  lst: Array<any>,
  fn: (item: any) => boolean
): boolean => {
  if (lst.length === 0) return true;
  if (!fn(lst[0])) return false;

  return mapand(lst.slice(1), fn);
};

export const mapor = (lst: Array<any>, fn: (item: any) => boolean): boolean => {
  if (lst.length === 0) return false;
  if (fn(lst[0])) return true;

  return mapor(lst.slice(1), fn);
};

// 功率公式
export const powerFormula = (point: DataPoint, eff: number): number => {
  const [q, h] = point;

  if (eff === 0) {
    return 0;
  }

  if (q <= 0 || h <= 0) {
    return 0;
  }

  return (q * h) / 367.2 / eff;
};

export const createQP = (
  QH: ReadonlyArray<DataPoint>,
  QEFF: ReadonlyArray<DataPoint>,
  sg: number = 1 // 比重
): Array<DataPoint> => {
  return QH.map((point, i) => {
    return [point[0], powerFormula(point, QEFF[i][1] / 100) * sg];
  });
};

// 分析数据集
export const parseDataset = (dataset: Dataset): DataSource => {
  const lsQEffType = window.localStorage.getItem("qEffType");
  const qEffType = lsQEffType || '3';
  const finalQEffType = Number(qEffType);

  const QH = genRegressionModal(zip(dataset.Q, dataset.H));
  const QEFF = genRegressionModal(zip(dataset.Q, dataset.EFF), false, false, finalQEffType);
  const QP = createQP(QH.points, QEFF.points);

  let result: DataSource = {
    name: dataset.name,
    type: dataset.type,
    frequency: dataset.frequency,
    diameter: dataset.diameter,
    speed: dataset.speed,
    range: dataset.range,
    QH,
    QP,
    QEFF,
  };

  if (dataset.NPSH) {
    // result.QNPSH = genRegressionModal(zip(dataset.Q, dataset.NPSH));
    const QNPSH: any = generateCatmullRomPoints(zip(dataset.Q, dataset.NPSH), 500);
    result.QNPSH = QNPSH;
  }

  if (dataset.diameter) {
    result.diameter = dataset.diameter;
  }

  return result;
};

// 生成数据
export const generateData = (
  equation: (x: number) => any,
  start: number,
  end: number,
  step: number,
  cbrt: boolean = false,
  isSelected: boolean = true
): DataPoint[] => {
  const data: DataPoint[] = [];

  for (let x = start; x < end; x += step) {
    if (cbrt) {
      let finalY = equation(x);

      if (x > 0) {
        const finalX = Math.sqrt(Math.sqrt(x));
        
        if (finalY > 0) {
          if (isSelected) {
            finalY = Math.cbrt(finalY);
          } else {
            finalY = Math.sqrt(Math.cbrt(finalY));
          }
  
          data.push([finalX, finalY]);
        }
      }
  
      if (x + step >= end) {
        let finalEndY = equation(end);
        const finalEndX = Math.sqrt(Math.sqrt(end));

        if (finalEndY > 0) {
          if (isSelected) {
            finalEndY = Math.cbrt(finalEndY);
          } else {
            finalEndY = Math.sqrt(Math.cbrt(equation(end)));
          }

          data.push([finalEndX, finalEndY]);
        }
      }
    } else {
      data.push([x, equation(x)]);
  
      if (x + step >= end) {
        data.push([end, equation(end)]);
      }
    }
  }

  return data;
};

// echart custom 文本
export const formatTextLine = (
  label: string,
  n: number,
  unit: string | undefined
) => {
  if (unit) {
    return `${label}: ${n} ${unit}`;
  }

  return `${label}: ${n}`;
};

// 10, 1 -> [10, 9, 8, 7, 6, 5, 4, 3, 2, 1]
export const rangeRight = (n: number, startAt: number = 0) => {
  const result = [];

  while (n > 0) {
    result.push(--n + startAt);
  }

  return result;
};

export const getSizeUnit = (size: number | undefined | null): "mm" | "%" => {
  return size ? "mm" : "%";
};

// 根据交点 生成 K
export const createK = (q: number, end: number, type: number = 0): number => {
  if (type !== 0) {
    return Math.sqrt(q / end);
  }

  return q / end;
};

export const stockMap: any = {
  all: undefined,
  outOfStock: 0,
  inStock: 1,
};

export const isSpuPointNotEmpty = (spu: ProccessedSpuData): boolean => {
  const ql = spu.Q.length;
  const hl = spu.H.length;
  const effl = spu.EFF.length;
  const npshl = spu.NPSH.length;

  if (npshl > 0) return Boolean(ql && hl && effl && npshl);
  return Boolean(ql && hl && effl);
};

export const parseSelected = (strList: string[] | null) => {
  if (strList === null || (strList.length === 1 && strList[0] === '')) {
    return []
  };

  return strList.map((str) => parseInt(str, 10));
};

// 创建辅助线数据
export const createAuxiliaryData = (
  q: number,
  h: number,
  start: number,
  end: number,
  type: number
) => {
  let result, formula;

  if (type !== 0) { // 公式2
    formula = (x: number) =>
      Math.sqrt((Math.pow(h, 2) / Math.pow(q, 3)) * x ** 3);
  } else { // 公式1
    formula = (x: number) => (h / q ** 2) * x ** 2;
  }

  result = generateData(formula, start, end, 0.1);

  return result;
};

// 根据 K, 之前的 QH, 公式 生成 QH2
export const createQH2Data = (
  k: number,
  oldQHData: DataPoint[],
  type: number
): DataPoint[] => {
  let newQHData: DataPoint[];

  if (type !== 0) {
    newQHData = oldQHData.map((p) => {
      return [p[0] * k ** 2, p[1] * k ** 3];
    });
  } else {
    newQHData = oldQHData.map((p) => {
      return [p[0] * k, p[1] * k ** 2];
    });
  }

  return newQHData;
};

// 根据 K, 之前的 QEFF, 公式 生成 QEFF2
export const createQEFF2Data = (
  k: number,
  oldQEFFData: DataPoint[],
  type: number = 0
) => {
  let newQEFFData: DataPoint[];
  const cbrtK = Math.cbrt(k);

  if (type !== 0) {
    newQEFFData = oldQEFFData.map((p) => [p[0] * k ** 2, p[1] * cbrtK]);
  } else {
    newQEFFData = oldQEFFData.map((p) => [p[0] * k, p[1] * cbrtK]);
  }

  return newQEFFData;
};

export const createQNPSH2Data = (
  k: number,
  oldQNPSHData: DataPoint[],
  type: number
) => {
  if (!oldQNPSHData || oldQNPSHData.length === 0) {
    return [];
  }

  let newQNPSHData: DataPoint[];

  if (type !== 0) {
    newQNPSHData = oldQNPSHData.map((p) => [p[0] * k ** 2, p[1] * k ** 2]);
  } else {
    newQNPSHData = oldQNPSHData.map((p) => [p[0] * k, p[1] * k ** 2]);
  }

  return newQNPSHData;
};

// 创建新的叶轮直径 [计算叶轮尺寸]
export const createDiameterData = (k: number, size: number, type: number) => {
  let diameter = 0;
  let diameterUnit = "mm";

  if (size) {
    if (type !== 0) {
      diameter = size * k;
    } else {
      diameter = size;
    }
  } else {
    diameterUnit = "%";

    if (type !== 0) {
      diameter = k * 100;
    } else {
      diameter = 100;
    }
  }

  return {
    diameter,
    diameterUnit
  };
};

// 创建新的转速 [计算转速]
export const createSpeedData = (k: number, speed: number, type: number) => {
  if (type !== 0) {
    return speed;
  }
  return speed * k;
};

// 计算频率
export const createFrequencyData = (
  k: number,
  frequency: number,
  type: number
) => {
  if (type === 0) {
    return frequency * k;
  }

  return frequency;
};

export const convertEFF1ToEFF2 = (
  oldQEFFObject: Result,
  oldQ: number, // 交点
  k: number
) => {
  return oldQEFFObject.predict(oldQ)[1] * Math.cbrt(k);
}

interface SpuPointsDataSourceBase {
  QH: CurveModel;
}

interface SpuPointsDataSource extends SpuPointsDataSourceBase {
  QP: DataPoint[];
  QEFF: CurveModel;
  QNPSH?: CurveModel;
}

// 解析spu 点 数据
export const parseSpuPointsDataset = (
  dataset: SpuPointsDataset
): SpuPointsDataSource => {
  const lsQEffType = window.localStorage.getItem("qEffType");
  const qEffType = lsQEffType || '3';
  const finalQEffType = Number(qEffType);

  const QH = genRegressionModal(zip(dataset.Q, dataset.H));
  const QEFF = genRegressionModal(zip(dataset.Q, dataset.EFF), false, false, finalQEffType);
  const QP = createQP(QH.points, QEFF.points);

  // let QH: any = dataset.H && generateCatmullRomPoints(zip(dataset.Q, dataset.H), 500);
  // let QEFF: any = dataset.EFF && generateCatmullRomPoints(zip(dataset.Q, dataset.EFF), 500);

  // const QHP = QH.dummyPoints;
  // const QEFFP = QEFF.dummyPoints;

  // const QP = createQP(QHP, QEFFP);
  
  // let QNPSH: any = dataset.NPSH && genRegressionModal(zip(dataset.Q, dataset.NPSH));
  let QNPSH: any = null;

  if (dataset.NPSH) {
    const zipNPSH = zip(dataset.Q, dataset.NPSH);

    const finalNpsh = zipNPSH.filter(v => {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const [e, i] = v;

      if (i) {
        return true;
      }

      return false;
    });

    if (finalNpsh.length < 2) {
      QNPSH = dataset.NPSH && generateCatmullRomPoints(zipNPSH, 500);
    } else {
      QNPSH = dataset.NPSH && generateCatmullRomPoints(finalNpsh, 500);
    }
  }

  if (dataset.NPSH && QNPSH) {
    let NPSH = dataset.NPSH;
    let Q = dataset.Q;

    let index = -1;

    for (let i = 0; i < NPSH.length; i++) {
      const v: any = NPSH[i]

      if (v === '') {
        index = i;
      } else {
        break;
      }
    }

    if (index > -1) {
      Q = Q.filter((v: any, i: any) => i > index);
    }

    let min = _.min(Q) || 0;

    if (QNPSH) {
      let dummyPoints = QNPSH.dummyPoints;

      if (dummyPoints) {
        dummyPoints = dummyPoints.filter((v: any) => {
          if (v) {
            const value = v[0];

            if (value > min) {
              return true
            }

            return false
          }

          return false
        });
      }

      QNPSH.dummyPoints = dummyPoints;
    }
  }

  const result = { QH, QP, QEFF, QNPSH };

  return result;
};

// 过滤相近数据
export const filterCloseArray = (arr: any) => {
  let finalArr: any = arr;

  const length = arr.length;

  const times = Math.ceil(length / 1000) * 2; // 循环次数

  for (let j = 0; j < Number(times); j++) {
    const forArr: any = finalArr;
    finalArr = [];

    for (let i in forArr) {
      const index = Number(i);

      if ((index % 2) === 1) { // 奇数 continue
        continue;
      }
  
      const now = forArr[index];
      const next = forArr[index + 1];

      const {
        Q = [],
        H = []
      } = now || {};
  
      const {
        Q: NQ = [],
        H: NH = []
      } = next || {};
  
      let close = true;
  
      if ((Q.length > 0 && NQ.length > 0) && Math.abs(Q.length - NQ.length) < 5) { // q 长度相近
        for (let ii in Q) {
          const qv = Q[ii] || 0;
          const nqv = NQ[ii] || 0;
  
          const dValue = Math.abs(qv - nqv);
  
          if (dValue > 200) {
            close = false;
            break;
          }
        }
  
        if (close) { // 上面校验通过了
          if (H.length > 0 && NH.length > 0) {
            for (let ii in H) {
              const hv = H[ii] || 0;
              const nhv = NH[ii] || 0;
      
              const dValue = Math.abs(hv - nhv);
      
              if (dValue > 400) {
                close = false;
                break;
              }
            }
          }
        }
      } else {
        close = false;
      }
  
      if (now) {
        finalArr.push(now);
      }
  
      if (!close) {
        if (next) {
          finalArr.push(next);
        }
      }
    }
  }

  return finalArr;
}

// 二次过滤相近数据
export const filterCloseTwoArray = (arr: any) => {
  let finalArr: any = arr;

  // 根据qtotal排序，这里只能过滤中间的数据
  finalArr = finalArr.sort((a: any, b: any) => {
    const {
      QTotal: aq
    } = a;

    const {
      QTotal: bq
    } = b;

    return aq - bq;
  });

  for (let j = 0; j < 5; j++) {
    const forArr: any = finalArr;
    finalArr = [];

    const length = forArr.length;
    const half: any = length / 2;
    const nHalf = parseInt(half);

    const hhalf: any = nHalf / 1.3;
    const nHhalf = parseInt(hhalf);

    const end = nHalf + nHhalf;
    
    for (let i = 0; i <= nHhalf; i++) {
      const now = forArr[i];

      finalArr.push(now);
    }

    for (let i = nHhalf; i < end; i++) {
      if ((i % 2) === 1) { // 奇数 continue
        continue;
      }

      const now = forArr[i];
      const next = forArr[i + 1];

      const {
        Q = [],
        H = []
      } = now || {};
  
      const {
        Q: NQ = [],
        H: NH = []
      } = next || {};
  
      let close = true;

      if ((Q.length > 0 && NQ.length > 0) && Math.abs(Q.length - NQ.length) < 5) { // q 长度相近
        for (let ii in Q) {
          const qv = Q[ii] || 0;
          const nqv = NQ[ii] || 0;
  
          const dValue = Math.abs(qv - nqv);
  
          if (dValue > 200) {
            close = false;
            break;
          }
        }
  
        if (close) { // 上面校验通过了
          if (H.length > 0 && NH.length > 0) {
            for (let ii in H) {
              const hv = H[ii] || 0;
              const nhv = NH[ii] || 0;
      
              const dValue = Math.abs(hv - nhv);
      
              if (dValue > 400) {
                close = false;
                break;
              }
            }
          }
        }
      } else {
        close = false;
      }
  
      if (now) {
        finalArr.push(now);
      }
  
      if (!close) {
        if (next) {
          finalArr.push(next);
        }
      }
    }

    for (let i = end; i < length; i++) {
      const now = forArr[i];

      finalArr.push(now);
    }
  }

  // 根据htotal排序，这里只能过滤中间的数据
  finalArr = finalArr.sort((a: any, b: any) => {
    const {
      HTotal: ah
    } = a;

    const {
      HTotal: bh
    } = b;

    return ah - bh;
  });

  for (let j = 0; j < 5; j++) {
    const forArr: any = finalArr;
    finalArr = [];

    const length = forArr.length;
    const half: any = length / 2;
    const nHalf = parseInt(half);

    const hhalf: any = nHalf / 1.3;
    const nHhalf = parseInt(hhalf);

    const end = nHalf + nHhalf;
    
    for (let i = 0; i <= nHhalf; i++) {
      const now = forArr[i];

      finalArr.push(now);
    }

    for (let i = nHhalf; i < end; i++) {
      if ((i % 2) === 1) { // 奇数 continue
        continue;
      }

      const now = forArr[i];
      const next = forArr[i + 1];

      const {
        Q = [],
        H = []
      } = now || {};
  
      const {
        Q: NQ = [],
        H: NH = []
      } = next || {};
  
      let close = true;

      if ((H.length > 0 && NH.length > 0) && Math.abs(H.length - NH.length) < 5) { // h 长度相近
        for (let ii in H) {
          const qv = Q[ii] || 0;
          const nqv = NQ[ii] || 0;
  
          const dValue = Math.abs(qv - nqv);
  
          if (dValue > 200) {
            close = false;
            break;
          }
        }
  
        if (close) { // 上面校验通过了
          if (H.length > 0 && NH.length > 0) {
            for (let ii in H) {
              const hv = H[ii] || 0;
              const nhv = NH[ii] || 0;
      
              const dValue = Math.abs(hv - nhv);
      
              if (dValue > 400) {
                close = false;
                break;
              }
            }
          }
        }
      } else {
        close = false;
      }
  
      if (now) {
        finalArr.push(now);
      }
  
      if (!close) {
        if (next) {
          finalArr.push(next);
        }
      }
    }

    for (let i = end; i < length; i++) {
      const now = forArr[i];

      finalArr.push(now);
    }
  }

  return finalArr;
}

export function isWindows() {
  const userAgent = window.navigator.userAgent.toLowerCase();
  return /windows nt/.test(userAgent);
}

export function addWindowsClazz() {
  if (isWindows()) {
    return 'aiko-is-windows';
  }

  return '';
}

// 获取url的参数
export function getUrlParam(url: any) {
  const requestParameters: any = {};

  if (!url) {
    return requestParameters;
  }

  const urlArr = url.split('?');

  if (urlArr[1]) {
    const urlParameters = urlArr[1].split('#')[0];

    if (urlParameters.indexOf('?') === -1) {
      const parameters = decodeURI(urlParameters);
      const parameterArray = parameters.split('&');

      for (let i = 0; i < parameterArray.length; i++) {
        requestParameters[parameterArray[i].split('=')[0]] = (parameterArray[i].split('=')[1]);
      }
    }
  }

  return requestParameters;
}

export function findClosestNumber(arr: any, num: any) {
  const qArr = arr.map((v: any) => v[0]);

  let closest = qArr[0];
  let difference = Math.abs(closest - num);
  let index = 0;
 
  qArr.forEach((v: any, i: number) => {
    const diff = Math.abs(v - num);
    if (diff < difference) {
      closest = v;
      difference = diff;
      index = i;
    }
  });
 
  return arr[index];
}

export function getImageInfo(src: string, callback: Function) {
  var image = new Image();
  image.crossOrigin = "anonymous";
  image.src = src;
  image.onload = function () {
    callback(image);
  };
}

// catmull rom 函数生成点
export function generateCatmullRomPoints(points: any[], numPoints: number) {
  if (points.length < 2) {
    return [];
  }

  // 扩展点数组，添加首尾虚拟点
  const extendedPoints = [points[0], ...points, points[points.length - 1]];
  const numSegments = points.length - 1; // 原始线段数目为点数减一
  const result = [];
  
  for (let index = 0; index < numPoints; index++) {
    // 计算全局参数t_global，范围从0到numSegments
    const t_global = (index * numSegments) / (numPoints - 1);
    let i = Math.floor(t_global);

    // 处理i超出范围的情况
    if (i >= numSegments) {
      i = numSegments - 1;
    }

    let t = t_global - i;

    // 确保在最后一个段时t不超过1
    if (i === numSegments - 1 && t > 1) {
      t = 1;
    }

    // 获取四个控制点
    const p0 = extendedPoints[i];
    const p1 = extendedPoints[i + 1];
    const p2 = extendedPoints[i + 2];
    const p3 = extendedPoints[i + 3];

    // 计算Catmull-Rom插值点
    const point = catmullRom(p0, p1, p2, p3, t);

    result.push(point);
  }

  return {
    dummyPoints: result
  };
}

export function catmullRom(p0: any, p1: any, p2: any, p3: any, t: number) {
  const t2 = t * t;
  const t3 = t2 * t;

  // 计算x
  const x = 0.5 * (
    (2 * p1[0]) +
    (-p0[0] + p2[0]) * t +
    (2 * p0[0] - 5 * p1[0] + 4 * p2[0] - p3[0]) * t2 +
    (-p0[0] + 3 * p1[0] - 3 * p2[0] + p3[0]) * t3
  );

  // 计算y
  const y = 0.5 * (
    (2 * p1[1]) +
    (-p0[1] + p2[1]) * t +
    (2 * p0[1] - 5 * p1[1] + 4 * p2[1] - p3[1]) * t2 +
    (-p0[1] + 3 * p1[1] - 3 * p2[1] + p3[1]) * t3
  );

  return [x, y];
}

function findClosestElement(arr: any[], target: number) {
  const current = arr.reduce((closest, current) => {
    const diff = Math.abs(current - target);

    if (diff < Math.abs(target - closest)) {
      return current;
    } else {
      return closest;
    }
  }, arr[0]); // 初始值设为数组的第一个元素

  return arr.indexOf(current);
}

// 现在主要用于npshr 就近原则取点
export function getNpshrByData(data: any[], q: number) {
  let result = 0;

  const x = data.map((v: any) => v[0]);

  const index = findClosestElement(x, q);

  const now = data[index];

  if (now) {
    result = now[1];
  }

  return result;
}

// base64转blob
export function dataURLtoBlob(url: string) {
  let arr: any = url.split(','), mime = arr[0].match(/:(.*?);/)[1],
      bstr = atob(arr[1]), i = bstr.length, u8arr = new Uint8Array(i)

  while(i--){
    u8arr[i] = bstr.charCodeAt(i)
  }

  return new Blob([u8arr], {type:mime})
}

// blob转baee64
export function readBlobAsDataURL(blob: Blob, callback: Function) {

  var a = new FileReader();

  a.onload = function(e: any) {callback(e.target.result);};

  a.readAsDataURL(blob);
}
